/* eslint-disable no-console */
import { useRuntimeConfig } from '#app'
import { ref, watch } from 'vue'

// Create refs but don't initialize values yet - default to not restricted
const isLoggingEnabled = ref(false)
const logLevel = ref<'debug' | 'log' | 'warn' | 'error'>('debug')
let isInitialized = false
let isConsoleOverridden = false

// Helper to determine if a log should be shown
function shouldLog(level: 'debug' | 'log' | 'warn' | 'error'): boolean {
  // In production, only show errors
  if (process.env.NODE_ENV === 'production') {
    return level === 'error'
  }

  // If logging is not restricted (no log level set), show everything
  if (!isLoggingEnabled.value) {
    return true
  }

  // For other log levels, respect the level
  const levels: ('debug' | 'log' | 'warn' | 'error')[] = ['debug', 'log', 'warn', 'error']
  const minLevel = levels.indexOf(logLevel.value)
  const currentLevel = levels.indexOf(level)
  return currentLevel >= minLevel
}

// Setup console overrides
function setupConsoleOverrides() {
  if (isConsoleOverridden || typeof window === 'undefined') return

  const originalConsole = {
    debug: console.debug,
    log: console.log,
    warn: console.warn,
    error: console.error,
  }

  console.debug = (...args: unknown[]) => {
    if (shouldLog('debug')) originalConsole.debug(...args)
  }
  console.log = (...args: unknown[]) => {
    // Special handling for Pinia logs (they use the 🍍 emoji)
    const isPiniaLog = args.some((arg) => typeof arg === 'string' && arg.includes('🍍'))
    if (isPiniaLog) {
      if (shouldLog('debug')) originalConsole.log(...args)
    } else {
      if (shouldLog('log')) originalConsole.log(...args)
    }
  }
  console.warn = (...args: unknown[]) => {
    if (shouldLog('warn')) originalConsole.warn(...args)
  }
  console.error = (...args: unknown[]) => {
    if (shouldLog('error')) originalConsole.error(...args)
  }

  isConsoleOverridden = true
}

function initializeFromStorage() {
  if (!isInitialized && typeof window !== 'undefined') {
    const storedLogLevel = localStorage.getItem('debug_log_level')

    // Set logging enabled state based on presence of log level
    isLoggingEnabled.value = storedLogLevel !== null

    // Set log level if it exists
    if (storedLogLevel) {
      logLevel.value = storedLogLevel as 'debug' | 'log' | 'warn' | 'error'
    }

    // Set up console overrides after loading stored settings
    setupConsoleOverrides()
    isInitialized = true
  }
}

// Initialize immediately if we're in the browser
if (typeof window !== 'undefined') {
  initializeFromStorage()
}

export const useLoggingControl = () => {
  let isProd = false

  try {
    const config = useRuntimeConfig()
    isProd = config.public?.isProd ?? false
  } catch (e) {
    // If we can't access runtime config, assume development
    isProd = process.env.NODE_ENV === 'production'
  }

  // Watch for changes and update localStorage
  watch(isLoggingEnabled, (newValue) => {
    if (typeof window !== 'undefined') {
      if (!newValue) {
        // When disabling restrictions, remove the log level
        localStorage.removeItem('debug_log_level')
        logLevel.value = 'debug' // Reset to default
      } else {
        // When enabling restrictions, store the current log level
        localStorage.setItem('debug_log_level', logLevel.value)
      }
    }
  })

  watch(logLevel, (newValue) => {
    if (typeof window !== 'undefined' && isLoggingEnabled.value) {
      localStorage.setItem('debug_log_level', newValue)
    }
  })

  const toggleLogging = () => {
    isLoggingEnabled.value = !isLoggingEnabled.value
  }

  const setLogLevel = (level: 'debug' | 'log' | 'warn' | 'error') => {
    logLevel.value = level
  }

  return {
    isLoggingEnabled,
    logLevel,
    toggleLogging,
    setLogLevel,
    isProd,
    shouldLog,
  }
}
